import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';

// GET público pro core. Diferente do scaffold da roletinha, aqui NÃO vão os
// headers de autenticação: a rota é aberta (visitante deslogado vê o
// histórico), e mandar token numa rota pública só empurra credencial pra um
// lugar que não pede.
//
// Duas outras diferenças, ambas por causa do polling:
// - o `signal` do chamador viaja junto, pra request em vôo morrer no unmount;
// - a falha é CLASSIFICADA (CrashApiError), porque 429 e 5xx pedem
//   comportamentos diferentes de retry e a decisão é do composable.

export type CrashApiErrorKind =
  // Rede caiu / DNS / CORS / timeout do próprio front.
  | 'network'
  // 422: `game_name` ausente ou fora da whitelist. É bug de chamada, não
  // condição de runtime — não adianta repetir a mesma request.
  | 'invalid_game'
  // 429: estourou o rate limit por IP (120 req/min). Em CGNAT o IP é
  // compartilhado, então dá pra tomar 429 sem ter exagerado sozinho.
  | 'rate_limited'
  // Qualquer outro !ok (5xx, 404 de rota, gateway).
  | 'api';

export class CrashApiError extends Error {
  constructor(
    public readonly kind: CrashApiErrorKind,
    // Segundos do header `Retry-After` quando presente (só em 429). null =
    // servidor não disse quanto esperar e o backoff local decide.
    public readonly retryAfterSeconds: number | null = null,
    message?: string,
  ) {
    super(message ?? kind);
    this.name = 'CrashApiError';
  }
}

type CrashApiGetOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;

  // O header aceita duas formas: segundos ou HTTP-date. O framework manda
  // segundos, mas ler as duas custa três linhas e evita esperar 0s por causa
  // de um proxy no meio do caminho.
  const seconds = Number(header);

  if (Number.isFinite(seconds)) return Math.max(0, seconds);

  const date = Date.parse(header);

  if (Number.isNaN(date)) return null;

  return Math.max(0, Math.round((date - Date.now()) / 1000));
}

export async function crashApiGet(
  path: string,
  { signal, timeoutMs = 10000 }: CrashApiGetOptions = {},
): Promise<any> {
  // Controller próprio pro timeout, encadeado no signal de fora: assim o
  // unmount aborta a request e o timeout também, sem precisar de
  // AbortSignal.any (que ainda não está em todo browser que atendemos).
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const abortFromCaller = () => controller.abort();

  if (signal) {
    if (signal.aborted) controller.abort();
    signal.addEventListener('abort', abortFromCaller, { once: true });
  }

  // O timeout e o listener do chamador só são desarmados DEPOIS de ler o corpo:
  // desarmar na volta do `fetch` deixaria um corpo que nunca termina de chegar
  // sem cobertura nenhuma — e um `await` que nunca resolve mata a corrente de
  // polling em silêncio, sem erro na tela pra explicar.
  try {
    let response: Response;

    try {
      response = await fetch(`${getApostatudoApiUrl()}${path}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
    } catch (error) {
      // Aborto pedido pelo chamador não é falha: propaga o AbortError cru pra
      // que o composable ignore a resposta sem marcar erro na tela.
      if (signal?.aborted) throw error;

      throw new CrashApiError(
        'network',
        null,
        `Crash API GET ${path} failed: network`,
      );
    }

    if (!response.ok) {
      if (response.status === 422) {
        throw new CrashApiError(
          'invalid_game',
          null,
          `Crash API GET ${path} failed: 422`,
        );
      }

      if (response.status === 429) {
        throw new CrashApiError(
          'rate_limited',
          parseRetryAfter(response.headers.get('Retry-After')),
          `Crash API GET ${path} failed: 429`,
        );
      }

      throw new CrashApiError(
        'api',
        null,
        `Crash API GET ${path} failed: ${response.status}`,
      );
    }

    try {
      return await response.json();
    } catch (error) {
      if (signal?.aborted) throw error;

      // Corpo cortado no meio (timeout, conexão caindo) é falha de rede; corpo
      // completo que não é JSON é falha da API. A diferença importa: só a
      // primeira merece a retentativa rápida do polling.
      throw new CrashApiError(
        controller.signal.aborted ? 'network' : 'api',
        null,
        `Crash API GET ${path} failed: invalid body`,
      );
    }
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}
